import { GridCellParams } from '@mui/x-data-grid';
import { t } from 'i18next';
import { cloneDeep, debounce } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import {
  mayaCepCreateRoute,
  mayaCiViewRoute, mayaCustomerInitiativeCreateRoute,
  mayaCustomerInitiativeDuplicateRoute,
  mayaCustomerInitiativeEditRoute,
} from 'basics/constants/routes.constants';
import { AllValidTherapeuticArea } from 'basics/enums/biogen.enums';
import { CiStatus, VeevaSyncStatus } from 'basics/enums/maya.enums';
import { useSetCiStatusToActiveMutation } from 'basics/graphql/mutations/setCiStatusToActive';
import { useSetCiStatusToDeletedMutation } from 'basics/graphql/mutations/setCiStatusToDeleted';
import { useSyncCiToVeevaMutation } from 'basics/graphql/mutations/syncCiToVeeva';
import useGetAllCustomerInitiativeQuery from 'basics/graphql/queries/getAllCustomerInitiatives';
import { SelectOption } from 'basics/options/options.types';
import { ProductsOptionType } from 'basics/types/products.types';
import { errorToast, succesToast, warningToast } from 'basics/utils/toast';
import { useCurrentUserState } from 'contexts/user';
import { CustomerInitiative, SortFilter } from 'generated/maya.types';
import { useQueryParams } from 'hooks/useQueryParams';
import useCustomerInitiativeListConfig from 'routes/AuthenticatedApp/CustomerInitiativeList/CustomerInitiativeList.config';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCustomerInitiativeList = () => {
  const navigate = useNavigate();
  const [ciSortFilter, setCiSortFilter] = useState(SortFilter.NEWEST);
  const [ciTitleFilter, setCiTitleFilter] = useState<(string)>();
  const [ciTherapeuticAreaFilter, setCiTherapeuticAreaFilter] = useState<string[]>([]);
  const [ciProductFilter, setCiProductFilter] = useState<string[]>([]);
  const [cis, setCis] = useState<CustomerInitiative[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<{
    therapeuticArea: SelectOption[];
    product: SelectOption[];
  }>({
    therapeuticArea: [],
    product: [],
  });
  const hasActiveFilters = Object.values(selectedFilters).some((options) => options.length > 0);
  const [setCiStatusToDeleted, { loading: removeCiLoading }] = useSetCiStatusToDeletedMutation();
  const [syncCiToVeeva] = useSyncCiToVeevaMutation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);
  const [open, setOpen] = useState(false);
  const [rowId, setRowId] = useState('');
  const { user } = useCurrentUserState();
  const { year } = useQueryParams();
  const [setCiStatusToActive] = useSetCiStatusToActiveMutation();
  const { setValue, providerFields } = useYodaCreateForm();

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const queryFilters = useMemo(
    () => ({
      title: ciTitleFilter,
      isTemplate: false,
      status: [CiStatus.ACTIVE, CiStatus.DRAFT],
      countries: user?.country ? [user?.country] : undefined,
      therapeuticArea: ciTherapeuticAreaFilter.length ? ciTherapeuticAreaFilter : null,
      product: ciProductFilter.length ? ciProductFilter : null,
      year: year ? parseInt(year, 10) : undefined,
    }),
    [ciTitleFilter, ciTherapeuticAreaFilter, ciProductFilter, user?.country, year],
  );

  const { data, loading, count } = useGetAllCustomerInitiativeQuery(queryOptions, queryFilters, ciSortFilter);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!loading) {
      setCis(data);
    }
  }, [data, loading]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handleChangeCiTitle = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setCiTitleFilter(event.target.value);
  };
  const debouncedHandleChangeCiTitle = useMemo(
    () => debounce(handleChangeCiTitle, 750),
    [],
  );

  const editCiCallback = async (ciId: string) => {
    navigate(`${mayaCustomerInitiativeEditRoute}?id=${ciId}`);
  };

  const duplicateCiCallback = async (ciId: string) => {
    navigate(`${mayaCustomerInitiativeDuplicateRoute}?cepSourceId=${ciId}`);
  };

  const addChildAction = async (ciId: string) => {
    navigate(`${mayaCepCreateRoute}?parentId=${ciId}`);
  };

  const deleteCiCallback = async (ciId: string) => {
    const { data: ciDeleted, error: cidDeletedError } = await setCiStatusToDeleted(ciId);
    if (ciDeleted && !cidDeletedError) {
      succesToast(t('maya_action_deleting_ci_success'));
      const clonedCis = cloneDeep(cis);
      const targetCiIndex = clonedCis.findIndex((ci) => { return ci._id === ciId; });
      if (targetCiIndex !== -1) {
        clonedCis.splice(targetCiIndex, 1);
        setCis(clonedCis);
      }
    } else {
      warningToast(cidDeletedError || t('errors_standard'));
    }

    setOpen(false);
  };

  const approveCiCallback = async (ciId: string) => {
    const isCiApproved = await setCiStatusToActive(ciId);

    if (isCiApproved.data) {
      succesToast(t('maya_action_activation_ci_success'));
      const clonedCis = cloneDeep(cis);
      const targetCiIndex = clonedCis.findIndex((ci) => { return ci._id === ciId; });

      if (targetCiIndex !== -1) {
        const updatedTargetCi = cloneDeep(clonedCis[targetCiIndex]);
        updatedTargetCi.status = CiStatus.ACTIVE;
        clonedCis.splice(targetCiIndex, 1, updatedTargetCi);
        setCis(clonedCis);
      }
    } else if (isCiApproved.error) {
      errorToast(isCiApproved.error);
    }
  };

  const syncCiCallback = async (ciId: string) => {
    const clonedCis = cloneDeep(cis);
    const targetCiIndex = clonedCis.findIndex((ci) => { return ci._id === ciId; });
    const updatedTargetCi = cloneDeep(clonedCis[targetCiIndex]);

    const { data: ciSynched, error: ciSynchedError } = await syncCiToVeeva(ciId);
    if (ciSynched && !ciSynchedError) {
      succesToast(t('maya_action_sync_ci_success'));
      updatedTargetCi.veevaSyncStatus = VeevaSyncStatus.SYNC;
    } else {
      warningToast(ciSynchedError || t('maya_action_sync_ci_error'));
      updatedTargetCi.veevaSyncStatus = VeevaSyncStatus.ERROR;
    }

    clonedCis.splice(targetCiIndex, 1, updatedTargetCi);
    setCis(clonedCis);
  };

  const openModalCallback = (ciId: string) => {
    setOpen(true);
    setRowId(ciId);
  };

  const {
    customerInitiativeListColumnConfig,
    ciTitleFieldConfig,
    ciTherapeuticAreaFieldConfig,
    ciProductFieldConfig,
    ciSortFieldConfig,
  } = useCustomerInitiativeListConfig(
    editCiCallback,
    duplicateCiCallback,
    addChildAction,
    openModalCallback,
    syncCiCallback,
    approveCiCallback,
    ciTherapeuticAreaFilter,
  );

  const newCustomerInitiativeClick = () => {
    navigate(mayaCustomerInitiativeCreateRoute);
  };

  const handleChangeCiTherapeuticArea = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    if (value.length === 0) {
      setCiProductFilter([]);
      setValue(ciProductFieldConfig.name, []);
      setSelectedFilters((prev) => ({ ...prev, product: [] }));
    }

    const values = value.map((element) => element.id as string);

    if (values.includes(AllValidTherapeuticArea.BBU)) {
      values.push(AllValidTherapeuticArea.BIOSIMS);
    }

    setCiTherapeuticAreaFilter(values);
    setSelectedFilters((prev) => ({ ...prev, therapeuticArea: value }));
  }, [setValue, setCiTherapeuticAreaFilter, ciProductFieldConfig.name]);

  const handleChangeCiProduct = useCallback((event: SyntheticEvent<Element, Event>, products: ProductsOptionType[]) => {
    const values = products.map((product) => product.id as string);
    setCiProductFilter(values);
    setSelectedFilters((prev) => ({ ...prev, product: products }));
  }, [setCiProductFilter]);

  const handleClick = (params: GridCellParams) => {
    if (params.field !== 'action') {
      navigate(`${mayaCiViewRoute}?id=${params.row._id}`);
    }
  };

  const customerInitiativeListTitle = t('maya_ci_list_title', { year });

  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: t('maya_main_menu_customer_initiative_label') }),
    newElementClick: newCustomerInitiativeClick,
  };

  const handleRemoveChip = useCallback((filterType: 'therapeuticArea' | 'product', idToRemove: string | number) => {
    if (filterType === 'therapeuticArea') {
      const newOptions = selectedFilters.therapeuticArea.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id as string);

      if (values.includes(AllValidTherapeuticArea.BBU)) {
        values.push(AllValidTherapeuticArea.BIOSIMS);
      }

      setCiTherapeuticAreaFilter(values);
      setSelectedFilters((prev) => ({ ...prev, therapeuticArea: newOptions }));
      setValue(ciTherapeuticAreaFieldConfig.name, newOptions);

      if (newOptions.length === 0) {
        setCiProductFilter([]);
        setSelectedFilters((prev) => ({ ...prev, product: [] }));
        setValue(ciProductFieldConfig.name, []);
      }
    } else if (filterType === 'product') {
      const newOptions = selectedFilters.product.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((product) => product.id as string);

      setCiProductFilter(values);
      setSelectedFilters((prev) => ({ ...prev, product: newOptions }));
      setValue(ciProductFieldConfig.name, newOptions);
    }
  }, [selectedFilters, setValue, ciTherapeuticAreaFieldConfig.name, ciProductFieldConfig.name]);

  const handleReset = useCallback(() => {
    setCiTherapeuticAreaFilter([]);
    setCiProductFilter([]);
    setSelectedFilters({
      therapeuticArea: [],
      product: [],
    });
    setValue(ciTherapeuticAreaFieldConfig.name, []);
    setValue(ciProductFieldConfig.name, []);
  }, [setValue, ciTherapeuticAreaFieldConfig.name, ciProductFieldConfig.name]);

  const hasSelectedFilters = selectedFilters.therapeuticArea.length > 0 || selectedFilters.product.length > 0;

  const handleSortChange = useCallback((value: SelectOption) => {
    setCiSortFilter(value.id as SortFilter);
  }, []);

  return {
    cis,
    ciTitleFieldConfig,
    ciTherapeuticAreaFieldConfig,
    ciProductFieldConfig,
    customerInitiativeListColumnConfig,
    debouncedHandleChangeCiTitle,
    handleChangeCiTherapeuticArea,
    handleChangeCiProduct,
    deleteCiCallback,
    handleClick,
    loading,
    customerInitiativeListTitle,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    open,
    removeCiLoading,
    rowCountState,
    rowId,
    setOpen,
    providerFields,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    ciSortFieldConfig,
    handleSortChange,
    hasActiveFilters,
    ciSortFilter,

    // Returned for Dashboards
    editCiCallback,
    duplicateCiCallback,
    addChildAction,
    openModalCallback,
    syncCiCallback,
    approveCiCallback,
  };
};

export default useCustomerInitiativeList;
